import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WiredCableQualityChart = ({ data = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    const series = Array.isArray(data)
      ? data
      : Array.isArray(data?.series)
        ? data.series
        : [];

    if (!series.length) return [];

    const sortedSeries = [...series].sort((a, b) => {
      const timeA = formatUtcToBrowserDate(a?.hour);
      const timeB = formatUtcToBrowserDate(b?.hour);

      return (timeA?.valueOf() || 0) - (timeB?.valueOf() || 0);
    });

    const lastTime = formatUtcToBrowserDate(
      sortedSeries[sortedSeries.length - 1]?.hour,
    );

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    const dataMap = new Map();

    sortedSeries.forEach((item) => {
      if (!item?.hour) return;

      const browserTime = formatUtcToBrowserDate(item.hour);

      if (!browserTime?.isValid()) return;

      const hour = browserTime.format("YYYY-MM-DD HH");

      dataMap.set(hour, {
        errorPorts: Number(item.errorPorts) || 0,
        normalPorts: Number(item.normalPorts) || 0,
      });
    });

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");
      const currentData = dataMap.get(key);

      return {
        label: currentTime.format("HH:mm"),
        fullTime: currentTime.format("YYYY/MM/DD HH:mm"),
        errorPorts: currentData?.errorPorts ?? 0,
        normalPorts: currentData?.normalPorts ?? 0,
      };
    });
  }, [data]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart =
      echarts.getInstanceByDom(chartRef.current) ||
      echarts.init(chartRef.current);

    chartInstanceRef.current = chart;

    const xAxisData = chartData.map((item) => item.label);
    const errorData = chartData.map((item) => item.errorPorts);
    const normalData = chartData.map((item) => item.normalPorts);

    const maxValue = Math.max(
      ...chartData.map((item) => item.errorPorts + item.normalPorts),
      0,
    );

    const yAxisMax = Math.max(10, Math.ceil(maxValue / 10) * 10);

    const option = {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      legend: {
        top: 0,
        left: 68,
        itemWidth: 12,
        itemHeight: 12,
        icon: "roundRect",
        selectedMode: true,
        textStyle: {
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        },
        data: ["Error Detected", "Normal"],
      },
      grid: {
        top: 42,
        left: 40,
        right: 20,
        bottom: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter: (params) => {
          const dataIndex = params?.[0]?.dataIndex ?? 0;
          const currentData = chartData[dataIndex] || {};

          return `
            <div style="min-width:140px;">
              <div style="font-weight:600;">${currentData.fullTime || ""}</div>
              <div>Error Detected: ${currentData.errorPorts ?? 0}</div>
              <div>Normal: ${currentData.normalPorts ?? 0}</div>
            </div>
          `;
        },
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: true,
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        interval: yAxisMax / 5,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Error Detected",
          type: "bar",
          stack: "ports",
          data: errorData,
          barWidth: 20,
          itemStyle: {
            color: theme.palette.primary.main,
          },
        },
        {
          name: "Normal",
          type: "bar",
          stack: "ports",
          data: normalData,
          barWidth: 20,
          itemStyle: {
            color: theme.palette.primary.blueSecound,
          },
        },
      ],
    };

    chart.setOption(option, true);
    chart.resize();

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, theme]);

  return (
    <Box sx={{ width: "100%" }}>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height: "320px",
        }}
      />
    </Box>
  );
};

export default WiredCableQualityChart;